import type {
  AutoInventoryStepsModern,
  InventoryScrapped,
  RoomsWithInventory,
} from 'app/typings/autoInventory';
import { AutoInventorySteps, ModifierType } from 'app/typings/autoInventory';
import type { ExtranetPackage, ExtranetRoom } from 'app/typings/packages';
import { formatDateWithoutHour } from 'app/utils/dates';
import { addDays, eachDayOfInterval } from 'app/utils/dayjs';
import { computeCrossRate } from 'app/utils/price';

const SMART_INVENTORY_OVERBOOKING_LIMIT = 1;

export type PackageRuleValues = {
  packageName: string;
  packageId: number;
  discount?: number;
  lastMinuteDays?: number;
  lastMinuteDiscountValue?: number;
  lastMinuteIsActivated: boolean;
  discountIsActivated: boolean;
};

export type FormValues = {
  rooms: {
    [index: number]: ExtranetRoom & {
      AutoInventoryId?: number;
      AutoInventoryName?: string;
    };
  };
  matchOTAPrice?: boolean;
  customBar: {
    value?: number;
    suffix: ModifierType.FLAT | ModifierType.RATE;
  };
  overrideOpenings: boolean;
  packageRules: PackageRuleValues[];
};

export const checkIfRoomMappingIsValid = (values: FormValues) => {
  if (!values || typeof values.rooms !== 'object') {
    return false;
  }

  const roomValues = Object.values(values.rooms);

  const allUndefined = roomValues.every(
    (item) => item.AutoInventoryId === undefined
  );

  return roomValues.length > 0 && !allUndefined;
};

export const checkIfBarIsValid = (values: FormValues) => {
  if (!values) {
    return false;
  }

  const { matchOTAPrice, customBar } = values;

  return matchOTAPrice || !!(customBar.value && customBar.value > 0);
};

const verifyDiscount = (values: FormValues) => {
  const verifiedPackageValues = values.packageRules.map((pkgValues) => {
    const {
      discount,
      discountIsActivated,
      lastMinuteIsActivated,
      lastMinuteDays,
      lastMinuteDiscountValue,
    } = pkgValues;

    const isDiscountActive = discount && discountIsActivated;
    const isLastMinuteActive =
      lastMinuteIsActivated && lastMinuteDays && lastMinuteDiscountValue;

    return (
      (isDiscountActive && !lastMinuteIsActivated) ||
      (isLastMinuteActive && !discountIsActivated) ||
      (isDiscountActive && isLastMinuteActive)
    );
  });

  return !verifiedPackageValues.includes(false);
};

export const checkIfDiscountIsValid = (
  values: FormValues,
  discountStepHasError: boolean
) => {
  if (!values || discountStepHasError) {
    return false;
  }

  return verifyDiscount(values);
};

export type PackagePricesPreview = {
  packageId: number;
  staycationRates: number[];
  discounts: number[];
  packageValues: number[];
  colors: string[];
};

export const organizePreviewDataByDate = (
  data: InventoryScrapped[],
  packages: ExtranetPackage[],
  formData: FormValues
) => {
  const result = {
    dates: [] as string[],
    stocks: [] as number[],
    bars: [] as number[],
    packagePrices: packages.map((p) => ({
      packageId: p.id,
      staycationRates: [] as number[],
      discounts: [] as number[],
      packageValues: [] as number[],
      colors: [] as string[],
    })) as PackagePricesPreview[],
  };

  const getCustomBarPrice = (price: number) => {
    if (
      formData.customBar.value &&
      formData.customBar.suffix === ModifierType.RATE
    ) {
      return price / (1 + formData.customBar.value / 100);
    }

    return price - (formData.customBar.value ?? 0);
  };

  const getPreviewColor = (discount: number, stock: number): string => {
    if (discount < 18 || discount > 70) {
      return 'red';
    } else if (stock === 0) {
      return 'yellow';
    }

    return 'green';
  };

  data.forEach((item) => {
    result.dates.push(item.date);

    const stock =
      item.stock - SMART_INVENTORY_OVERBOOKING_LIMIT < 0
        ? 0
        : item.stock - SMART_INVENTORY_OVERBOOKING_LIMIT;

    const finalBar = getCustomBarPrice(item.price);

    result.stocks.push(stock);
    result.bars.push(finalBar);

    packages.forEach((pkg) => {
      const pkgIdx = result.packagePrices.findIndex(
        (pr) => pr.packageId === pkg.id
      );

      const packageValue = computeCrossRate(
        pkg.id,
        pkg.experiences,
        item.date,
        pkg.dayPackage,
        0
      );

      const prices =
        item.price > 0
          ? pricesCalculation(
              finalBar,
              formData,
              item.date,
              packageValue,
              pkgIdx
            )
          : { price: 0, staycationRate: 0, discount: 0 };

      result.packagePrices[pkgIdx].staycationRates.push(prices.staycationRate);
      result.packagePrices[pkgIdx].discounts.push(
        Math.round(prices.discount ?? 0)
      );
      result.packagePrices[pkgIdx].packageValues.push(
        packageValue + prices.price
      );
      result.packagePrices[pkgIdx].colors.push(
        getPreviewColor(Math.round(prices.discount ?? 0), stock)
      );
    });
  });

  return result;
};

export const createEmptyRoomInventory = () => {
  const now = Date.now();

  const dates = eachDayOfInterval({
    start: now,
    end: addDays(now, 6),
  });

  return dates.map((date) => ({
    date: formatDateWithoutHour(date),
    stock: 0,
    price: 0,
  }));
};

export const associateRooms = (
  formData: FormValues,
  roomsScrapped: RoomsWithInventory[]
) => {
  return Object.entries(formData.rooms).map(([idx, room]) => {
    const roomScrapped = roomsScrapped.find(
      (data) => data.roomId === room.AutoInventoryId
    );

    return {
      ...room,
      inventory: roomScrapped?.inventory ?? createEmptyRoomInventory(),
    };
  });
};

const pricesCalculation = (
  barPrice: number,
  formData: FormValues,
  date: string,
  packageValue: number,
  pkgIndex: number
) => {
  const crossedPrice = barPrice + packageValue;

  if (
    !formData.packageRules[pkgIndex].lastMinuteIsActivated &&
    formData.packageRules[pkgIndex].discount
  ) {
    return {
      price: barPrice,
      staycationRate: Math.floor(
        crossedPrice -
          (crossedPrice * (formData.packageRules[pkgIndex].discount || 0)) / 100
      ),
      discount: barPrice > 0 ? formData.packageRules[pkgIndex].discount : 0,
    };
  }

  if (
    (formData.packageRules[pkgIndex].lastMinuteDiscountValue &&
      formData.packageRules[pkgIndex].lastMinuteDays) ||
    formData.packageRules[pkgIndex].discount
  ) {
    const nextXDays = eachDayOfInterval({
      start: new Date(),
      end: addDays(
        new Date(),
        formData.packageRules[pkgIndex].lastMinuteDays ?? 0
      ),
      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    }).map((date) => formatDateWithoutHour(date));

    if (nextXDays.includes(date)) {
      return {
        price: barPrice,
        staycationRate: Math.floor(
          crossedPrice -
            (crossedPrice *
              (formData.packageRules[pkgIndex].lastMinuteDiscountValue ?? 0)) /
              100
        ),
        discount:
          barPrice > 0
            ? formData.packageRules[pkgIndex].lastMinuteDiscountValue
            : 0,
      };
    }

    return {
      price: barPrice,
      staycationRate: Math.floor(
        crossedPrice -
          (crossedPrice * (formData.packageRules[pkgIndex].discount ?? 0)) / 100
      ),
      discount: barPrice > 0 ? formData.packageRules[pkgIndex].discount : 0,
    };
  }

  return {
    price: barPrice,
    staycationRate: 0,
    discount: 0,
  };
};

export const getHDIWDetails = (
  step: AutoInventorySteps | AutoInventoryStepsModern,
  isLegacy = true
) => {
  const sharedEditable = {
    title: 'Editable',
    description:
      'You will still be able to edit your stocks and rates manually on specific dates.',
    emoji: '🤚',
  };

  const sharedAutomatic = {
    title: 'Automatic',
    description:
      'Your connected stocks and prices on mapped room categories will be automatically published for the next 8 weeks.',
    emoji: '🤖',
  };

  const sharedDynamic = {
    title: 'Dynamic',
    description: 'Stock and BAR data are updated 3 times a day.',
    emoji: '📈',
  };

  const sharedFlexible = {
    title: 'Flexible',
    description: `You can deactivate the Auto Inventory at any time and revert to your initial rates${
      isLegacy ? ' and discount' : ''
    }.`,
    emoji: '🔄',
  };

  const sharedPublicPrices = {
    title: 'Public prices',
    description: 'We read your public rate, not the Genius price.',
    emoji: '🙋‍♂️',
  };

  switch (step) {
    case AutoInventorySteps['room-mapping']:
      return [
        sharedEditable,
        {
          title: 'Safe',
          description:
            "We won't sell your last available stock. If there is 1 stock left on Booking, Staycation closes.",
          emoji: '⛑️',
        },
        sharedAutomatic,
        sharedDynamic,
        sharedFlexible,
      ];

    case AutoInventorySteps.bar:
    case AutoInventorySteps.discount:
      return [
        sharedPublicPrices,
        sharedEditable,
        sharedAutomatic,
        sharedDynamic,
        sharedFlexible,
      ];

    case AutoInventorySteps.preview:
      return [
        {
          title: 'Read only',
          description:
            'We only read your OTA stocks and prices. You will still need to manually enter reservations in your PMS.',
          emoji: '🤓',
        },
        ...(isLegacy
          ? [sharedEditable, sharedAutomatic, sharedDynamic, sharedFlexible]
          : []),
      ];

    default:
      return [];
  }
};
